import React, { useContext, useEffect, useMemo } from 'react'
import { Subject, of } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { Editor, Element, Node, Transforms } from 'slate'
import { ReactEditor } from 'slate-react'
import { Checkbox, Icon, IconButton, cn, makeStyles, useDynamicObservable } from '@cicero/ui'
import { LOGGER } from '@cicero/data/utils'
import { EditorContext, EditorService, IElementDefinition, IRenderElementProps } from '../EditorService'
import { ElementBlock } from './ParagraphElement'
import { PageDoc, PageInboxStatus, PageSchema, useModel } from 'models'

export const TaskListElement = ({ children, attributes }: IRenderElementProps<ITaskListElement>) => {
  const { taskListWrapper } = useStyles()
  return <ElementBlock attributes={attributes}>
    <ul className={taskListWrapper}>
      { children }
    </ul>
  </ElementBlock>
}

export const TaskListItemElement = ({ children, attributes, element }: IRenderElementProps<ITaskListItemElement>) => {
  const service = useContext(EditorContext)
  const Pages = useModel(PageSchema)
  const [page, subscribeToPage] = useDynamicObservable<PageDoc|null>(null)
  const changelog = useMemo(() => new Subject<Node>(), [])
  const { task, taskContent } = useStyles()

  // Attaches a page id to the editor block
  const setId = (id: string, node: Node) => {
    if (node.type === 'tasklist-item' && node.page_id !== id) {
      Transforms.setNodes(service.editor, { page_id: id }, { match: (n) => n === node })
    }
  }

  // Toggles the completed status
  const onToggle = (ev: any) => {
    const path = ReactEditor.findPath(service.editor, element)
    Transforms.setNodes(service.editor, { completed: ev.target.checked }, { at: path })
    if (page) {
      Pages.UPDATE(page, { inbox_status: ev.target.checked ? PageInboxStatus.pending : PageInboxStatus.archived })
    }
  }

  // Opens a task as a full page
  const openPage = async () => {
    if (page) {
      const p = await Pages.GET(page.id)
      Pages.active$.next(p)
    }
  }

  // Get a page for this element, or create a NEW one (as yet unsaved, discarded if there is never text)
  useEffect(() => {
    if (element.page_id) {
      subscribeToPage(Pages.GET$(element.page_id))
    } else {
      const title = Node.string(element)
      Pages.NEW({ title: title, parent_id: PageSchema.UTILS.pluckId(service.page), inbox_status: PageInboxStatus.pending }).then(p => {
        subscribeToPage(of(p))
      })
    }
  }, [element.page_id, service])

  // Listen to content changes, and set the page title
  useEffect(() => {
    const sub = changelog.pipe(
      debounceTime(1000)
    ).subscribe(async (node) => {
      const title = Node.string(node)
      if (page && page.title !== title) {
        await Pages.UPDATE(page, { title })
        setId(PageSchema.UTILS.pluckId(page), node)
      }
    })

    return () => sub.unsubscribe()
  }, [page])

  // When the element changes, emit a change to update the underlying page
  useEffect(() => {
    changelog.next(element)
  }, [element])

  return <li {...attributes} className={task}>
    <div contentEditable={false} style={{ marginLeft: `${(element.indent || 0) * 20}px` }}>
      <Checkbox checked={element.completed || false} onChange={(ev) => onToggle(ev)} />
    </div>
    <span className={taskContent}>
      {children}
    </span>
    <div contentEditable={false} className={cn({ hoverable: true })}>
      <IconButton><Icon>account_box</Icon></IconButton>
      <IconButton><Icon>schedule</Icon></IconButton>
      <IconButton onClick={() => {openPage()}}><Icon>arrow_forward_ios</Icon></IconButton>
    </div>
  </li>
}

const useStyles = makeStyles({
  taskListWrapper: {
    padding: 0,
  },
  task: {
    listStyleType: 'none',
    display: 'flex',
    alignItems: 'center',

    '& .MuiCheckbox-root': {
      padding: 6,
      marginRight: 6,
    },

    '& .MuiIconButton-root': {
      padding: 6,
    },

    '& .hoverable': {
      opacity: 0,
      transition: 'all ease-in-out .5s',
    },

    '&:hover': {
      '& .hoverable': {
        opacity: 1,
      },
    },
  },
  taskContent: {
    flexGrow: 1,
  },
})

export const TaskListElementDef: IElementDefinition = {
  renderer(props) { return <TaskListElement {...props} /> },
  // setup(service) {
  //   const { isVoid } = service.editor
  //   service.editor.isVoid = (el) => el.type === 'tasklist' ? true : isVoid(el)
  // },
  // async preRender(el: ITaskListElement, service, store) {
  //   const Pages = store.getModel(PageSchema)
  //   const ids = el.children.map((child: ITaskListItemElement) => child.page_id || undefined)
  //   const validIds = ids.filter(id => id)
  //   const pages = validIds.length ? await Pages.QUERY({ selector: { id: { $in: validIds } } }) : []
  //   const indexed = indexBy(pages, 'id', true)
  //   const tasks = await Promise.all(el.children.map(async (task, i) => {
  //     const id = ids[i]
  //     const pageIfExists = indexed[id || '']
  //     LOGGER.debug('prepping', task, indexed[id || ''], i)
  //     if (pageIfExists) {
  //       return {
  //         ...task,
  //         children: [{ text: pageIfExists.title }],
  //         page_id: PageSchema.UTILS.pluckId(pageIfExists),
  //       }
  //     } else {
  //       return task
  //     }
  //   }))

  //   return { ...el, children: tasks }
  // },
  // async preSave(el: ITaskListElement, service, store) {
  //   const Pages = store.getModel(PageSchema)
  //   const ids = el.children.map((child: ITaskListItemElement) => child.page_id || undefined)
  //   const validIds: string[] = ids.filter(id => id) as any
  //   const pages = validIds.length ? await Pages.QUERY(PageSchema.QUERIES.queryIds(validIds)) : []
  //   const indexed = indexBy(pages, 'id', true)
  //   const persistedTasks = await Promise.all(el.children.map(async (task, i) => {
  //     const id = ids[i]
  //     const pageIfExists = indexed[id || '']
  //     const title = Node.string(task)
  //     if (!title) { return task }

  //     if (pageIfExists) {
  //       if (pageIfExists.title === title) { return task }
  //       await pageIfExists.atomicUpdate((pg) => {
  //         pg.title = title
  //         return pg
  //       })
  //       return task
  //     } else {
  //       const page = await Pages.CREATE({ title, searchable: false, parent_id: service.page.id, inbox_status: PageInboxStatus.pending })
  //       return { ...task, page_id: page.id }
  //     }
  //   }))

  //   LOGGER.debug('saving list', el)
  //   return { ...el, children: persistedTasks }
  // },
}

export const TaskListItemElementDef: IElementDefinition = {
  commands: [
    { title: 'New Task', shortcut: 'enter', handler: (e, s) => TaskListCommands.insertNewTask(s) },
    { title: 'Indent Tasks', shortcut: 'tab', handler: (e, s) => TaskListCommands.indentTasks(s, 1) },
    { title: 'Deindent Tasks', shortcut: 'shift+tab', handler: (e, s) => TaskListCommands.indentTasks(s, -1) },
  ],
  renderer(props) { return <TaskListItemElement {...props} /> },
}

export const TaskListCommands = {
  /**
   * Add a new task, but remove the page_id etc from the previous line
   */
  insertNewTask(s: EditorService) {
    Editor.insertBreak(s.editor)
    for (const [node, path] of Editor.nodes(s.editor, { match: n => n.type === 'tasklist-item' })) {
      LOGGER.debug('cleaning', node)
      Transforms.unsetNodes(s.editor, ['page_id', 'completed'], { at: path })
    }
  },

  /**
   * Add an indent level to a task or group of tasks
   * @param s an editor service
   * @param change a positive or negative int for the number of places to indent
   */
  indentTasks(s: EditorService, change: number) {
    for (const [node, path] of Editor.nodes(s.editor, { match: n => n.type === 'tasklist-item' })) {
      const indent: number = node.indent as any || 0
      const newLevel = Math.max(indent + change, 0)
      if (newLevel > 0) {
        Transforms.setNodes(s.editor, { indent: newLevel }, { at: path })
      } else {
        Transforms.unsetNodes(s.editor, 'indent', { at: path })
      }
    }
  },

  insertTaskList(s: EditorService) {
    const list = { type: 'tasklist', children: [{ type: 'tasklist-item', children: [{ text: '' }] }] }
    Transforms.insertNodes(s.editor, list)
  },
}

export interface ITaskListElement extends Element {
  children: ITaskListItemElement[]
}

export interface ITaskListItemElement extends Element {
  completed?: boolean
  page_id?: string
  indent?: number
}
